/**
 * Real-time Analytics Store — P13-V5A-R3 · DIRECTOR_REPAIR_R1.
 *
 * WHAT THIS REPLACES. The canonical K4A socket authority admits CURRENT persisted `super_admin` only
 * and answers everyone else with the literal payload `{ denied: true }`
 * (`realtimeSocketServer.js:324,335`). This store accepted ANY truthy payload:
 *
 *     const payload = (data && data.metrics) ? data.metrics : data
 *     if (payload) { metrics.value = { ...payload, timestamp: ... } }
 *
 * so a refusal became `metrics`, `lastUpdated` was stamped, and the widget — whose only non-data
 * branch was `v-if="!metrics"` -> "Cargando métricas..." — rendered a populated grid of blanks under
 * an "Actualizado" timestamp. A REST 403 took the other path and left the widget loading forever.
 *
 * WHAT IS ENFORCED NOW. Four states that cannot be confused:
 *
 *   ready         a payload carrying at least one recognised telemetry key
 *   unauthorized  a socket `{ denied: true }`, or a REST 403 — an explicit refusal
 *   error         any other failure
 *   loading       only while a request is genuinely outstanding
 *
 * A denial is never data, never zero, and never an endless spinner. Frontend role awareness is UX
 * only: the server is the authority, and this store simply believes what it is told.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import websocketService from '@/services/websocket.service'
import { RT_LOCAL } from '@/services/realtime-events'
import { ApiClient } from '@/services/api-client'

export interface RealTimeMetrics {
  totalEvents: number
  eventsToday: number
  eventsThisHour: number
  eventsLastHour: number
  recentEvents: number
  eventsByType: Record<string, number>
  eventsByAction: Record<string, number>
  eventsByDepartment: Record<string, number>
  topUsers: Array<{ key: string; value: number }>
  timestamp?: string | Date
}

/** Keys that identify a payload as real telemetry. A payload with none of them is not metrics. */
const TELEMETRY_KEYS = [
  'totalEvents',
  'eventsToday',
  'eventsThisHour',
  'eventsLastHour',
  'recentEvents',
  'eventsByType',
  'eventsByAction'
] as const

/**
 * MERGE-SAFETY FENCE (P13-V5A-R3 · DIRECTOR_REPAIR_R1 §17).
 *
 * The REST alias `/realtime/analytics/metrics` is UNGATED on the current backend main: it answers
 * every authenticated actor with the full process-global counter set. This store now normalises the
 * backend's nested shape, so merging the frontend first would make it RENDER telemetry that the old
 * backend should never have served — a transient disclosure regression introduced by the repair
 * itself.
 *
 * So the consumer requires the repaired backend to identify itself. Only a response carrying the
 * canonical authority marker is rendered; a backend that does not stamp it is treated as an
 * incompatible contract and publishes nothing. The fence is additive, costs nothing once both sides
 * are merged, and makes the frontend-first order safe instead of merely convenient.
 */
export const REALTIME_AUTHORITY_MARKER = 'K4A_SOCKET_POLICY_SUPER_ADMIN_ONLY';

/** The canonical socket refusal. Recognised explicitly so it can never be mistaken for data. */
export function isDenialPayload(payload: any): boolean {
  return !!payload && typeof payload === 'object' && payload.denied === true
}

function hasTelemetryKeys(payload: any): boolean {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return false
  return TELEMETRY_KEYS.some((key) => key in payload)
}

/**
 * The backend emits `analyticsService.getAllMetrics()` on BOTH transports: a flat map of raw counter
 * keys (`api.requests`, `type_incident`, …) with the human-readable block nested under `summary`
 * (`backend/services/analyticsService.js:155-166`). The widget reads `totalEvents`, `eventsToday`,
 * … — which live in `summary`, NOT at the top level.
 *
 * The pre-repair store assigned the raw object straight to `metrics`, so those four fields were
 * `undefined` for EVERY actor, including an authorised one: the grid rendered four blanks under an
 * "Actualizado" timestamp. Normalising here means a legitimate reader finally sees its real numbers,
 * and a payload of neither shape is still refused rather than rendered empty.
 */
export function normalizeTelemetry(payload: any): any | null {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return null
  if (isDenialPayload(payload)) return null
  if (hasTelemetryKeys(payload.summary)) return { ...payload.summary }
  if (hasTelemetryKeys(payload)) return { ...payload }
  return null
}

export function isTelemetryPayload(payload: any): boolean {
  return normalizeTelemetry(payload) !== null
}

export const useRealTimeAnalyticsStore = defineStore('realtime-analytics', () => {
  const metrics = ref<RealTimeMetrics | null>(null)
  const loading = ref(false)
  const lastUpdated = ref<string | null>(null)
  const error = ref<string | null>(null)
  const trend = ref<Array<{ t: string; v: number }>>([])

  /** An explicit authorization refusal from the canonical authority (socket or REST). */
  const unauthorized = ref(false)
  /** True once this store has finished trying, however it ended. Prevents an endless "loading". */
  const resolved = ref(false)

  const isStale = computed(() => {
    if (!lastUpdated.value) return true
    const age = Date.now() - new Date(lastUpdated.value).getTime()
    return age > 60_000 // 1 minuto
  })

  /** The single state the widget renders from. */
  const status = computed<'loading' | 'unauthorized' | 'error' | 'ready' | 'empty'>(() => {
    if (unauthorized.value) return 'unauthorized'
    if (error.value) return 'error'
    if (metrics.value) return 'ready'
    if (loading.value || !resolved.value) return 'loading'
    return 'empty'
  })

  // PR-20: Guard contra doble registro de handlers
  let _handlersAttached = false

  /**
   * P13-V5A-R3 · DIRECTOR_FINAL_REPAIR_R2 — ACQUISITION OWNERSHIP.
   *
   * Telemetry belongs to the authorization context that obtained it. A canonical logout clears auth
   * state and disconnects the socket, but it does NOT destroy this Pinia instance: a second actor
   * logging into the same SPA used to mount the analytics view over the PREVIOUS actor's values, and
   * `status` evaluated `ready` from that cache BEFORE any new authorization answer arrived. Clearing
   * on `{ denied: true }` is too late — the render happens first.
   *
   * Each acquisition takes an epoch. Starting a new one invalidates the old telemetry immediately,
   * and any in-flight answer from a superseded epoch is discarded instead of published.
   */
  let acquisitionEpoch = 0

  /**
   * Drop everything owned by the previous authorization context.
   * Exposed so an explicit session transition may invoke it; `initialize()` already does.
   * This is NOT a second authentication authority — it only forgets, it never grants.
   */
  function reset() {
    metrics.value = null
    trend.value = []
    lastUpdated.value = null
    error.value = null
    unauthorized.value = false
    resolved.value = false
    loading.value = false
  }

  function _refuse() {
    unauthorized.value = true
    resolved.value = true
    loading.value = false
    // A refusal must not leave stale data on screen, and must not assert freshness.
    metrics.value = null
    trend.value = []
    lastUpdated.value = null
  }

  const _handleMetrics = (data: any) => {
    const payload = (data && data.metrics) ? data.metrics : data

    if (isDenialPayload(data) || isDenialPayload(payload)) {
      _refuse()
      return
    }
    // A payload with no recognisable telemetry key is not metrics: rendering it would produce a
    // grid of blanks that looks like a successful read.
    const normalized = normalizeTelemetry(payload)
    if (!normalized) {
      resolved.value = true
      return
    }

    metrics.value = {
      ...normalized,
      timestamp: normalized.timestamp || payload.timestamp || new Date().toISOString()
    }
    lastUpdated.value = new Date().toISOString()
    unauthorized.value = false
    error.value = null
    resolved.value = true
    loading.value = false
  }

  /**
   * ── P13-V5A-R6 · A SERIES IS ONLY THIS CONSUMER'S IF IT ANSWERS THIS CONSUMER'S QUESTION ──
   *
   * `analytics:timeseries` is a BROADCAST to the calling socket with no correlation id, and this
   * application mounts TWO independent consumers of it on one page: `/reports/analytics-suite`
   * renders `RealtimeAnalyticsWidget` (this store, which asks for `events`/`1h`) above
   * `RealtimeReports` (which asks for `events`/`10m`). Both listened to the same event and neither
   * checked what it had received, so each rendered the other's window: a trend labelled one
   * measurement period, drawn from another. A figure attributed to a period it was not measured
   * over is a false reporting fact, even though every number in it is real.
   *
   * The server DOES echo `{ metric, duration, data }` (`realtimeSocketServer.js:339`), so the
   * question each answer belongs to is already on the wire — no protocol change is required, and
   * none is made here. The consumer simply declines to bank a series it never asked for.
   *
   * The DENIAL path is deliberately left untargeted: `{ denied: true }` carries no metric or
   * duration, and a refusal is authority rather than data. Filtering it would let an authorisation
   * refusal be discarded as "not mine" — the one outcome that must never be dropped.
   */
  const REQUESTED_SERIES = { metric: 'events', duration: '1h' } as const

  /**
   * P13-V5A-R6 FINAL — attribution is REQUIRED, not merely checked when offered.
   *
   * The first repair accepted a success that stated neither field, on the reasoning that a producer
   * which does not echo the parameters should keep working. The repository disproves that producer:
   * `analytics:timeseries` has exactly ONE success emitter (`realtimeSocketServer.js:339`), which
   * always sends `{ metric, duration, data }`, and exactly one other emission, `{ denied: true }`.
   * The REST timeseries surface has no frontend consumer, and the REST fallback writes `metrics`,
   * never `trend`. So the branch preserved a hypothesis while leaving open the one payload that
   * could genuinely belong to the other consumer mounted on the same page.
   *
   * Both fields must match. Partial attribution is not attribution.
   */
  const _isOurSeries = (payload: any): boolean =>
    payload?.metric === REQUESTED_SERIES.metric && payload?.duration === REQUESTED_SERIES.duration

  const _handleTimeseries = (payload: any) => {
    if (isDenialPayload(payload)) {
      _refuse()
      return
    }
    if (!_isOurSeries(payload)) return
    if (payload && Array.isArray(payload.data)) {
      trend.value = payload.data
    }
  }

  function attachSocketHandlers() {
    if (_handlersAttached) return
    websocketService.on(RT_LOCAL.ANALYTICS_METRICS, _handleMetrics)
    websocketService.on(RT_LOCAL.ANALYTICS_TIMESERIES, _handleTimeseries)
    _handlersAttached = true
  }

  function detachSocketHandlers() {
    if (!_handlersAttached) return
    websocketService.off(RT_LOCAL.ANALYTICS_METRICS, _handleMetrics)
    websocketService.off(RT_LOCAL.ANALYTICS_TIMESERIES, _handleTimeseries)
    _handlersAttached = false
  }

  /**
   * The REST fallback, extracted so it is directly testable and so a refusal is terminal.
   *
   * `/realtime/analytics/metrics` carries the SAME authority as the socket, so a 403 here is the
   * same refusal by another transport — never an empty dataset and never zeros. No other producer
   * is attempted: there is no wider one to attempt.
   */
  async function loadRestFallback(expectedEpoch?: number): Promise<void> {
    const epoch = expectedEpoch ?? acquisitionEpoch
    // A fallback scheduled by a previous acquisition must not speak for the current one.
    if (epoch !== acquisitionEpoch) return
    if (unauthorized.value || metrics.value) {
      resolved.value = true
      loading.value = false
      return
    }
    try {
      const resp = await ApiClient.get<any>('/realtime/analytics/metrics')
      // The context may have changed while this request was in flight.
      if (epoch !== acquisitionEpoch) return
      const envelope = (resp as any)?.data ?? resp
      const payload = (resp as any)?.metrics ?? envelope?.metrics ?? envelope
      if (isDenialPayload(payload)) {
        _refuse()
        return
      }
      // The repaired backend stamps its authority. An unstamped response comes from a backend whose
      // realtime analytics alias is ungated, and must not be rendered.
      const marker = (resp as any)?.telemetry_scope?.authority ?? envelope?.telemetry_scope?.authority
      if (marker !== REALTIME_AUTHORITY_MARKER) {
        error.value = 'Contrato de telemetría en tiempo real no reconocido'
        return
      }
      const normalized = normalizeTelemetry(payload)
      if (normalized) {
        metrics.value = normalized as RealTimeMetrics
        lastUpdated.value = new Date().toISOString()
      }
    } catch (e: any) {
      if (epoch !== acquisitionEpoch) return
      if (e?.response?.status === 403 || e?.status === 403) {
        _refuse()
        return
      }
      error.value = e?.message || 'No fue posible obtener métricas iniciales'
    } finally {
      // Never settle the CURRENT context on behalf of a superseded acquisition.
      if (epoch === acquisitionEpoch) {
        loading.value = false
        resolved.value = true
      }
    }
  }

  async function initialize(): Promise<void> {
    // A new acquisition ALWAYS takes ownership, even if one is already in flight: the caller is a
    // (possibly different) session mounting the view, and the previous context's values must stop
    // being publishable before anything can render.
    const epoch = ++acquisitionEpoch
    reset()
    loading.value = true

    attachSocketHandlers()
    websocketService.requestAnalyticsMetrics()
    websocketService.requestAnalyticsTimeSeries('events', '1h')

    // Fallback vía REST si el socket no responde. `loading` permanece activo hasta que ALGO resuelve,
    // de modo que el widget nunca queda en una carga que ya no espera nada.
    setTimeout(() => { void loadRestFallback(epoch) }, 1500)
  }

  function refresh(): void {
    if (unauthorized.value) return   // a refused actor does not retry a producer that refused it
    websocketService.requestAnalyticsMetrics()
    websocketService.requestAnalyticsTimeSeries('events', '1h')
  }

  return {
    // state
    metrics,
    loading,
    lastUpdated,
    error,
    trend,
    unauthorized,
    resolved,

    // getters
    isStale,
    status,

    // actions
    initialize,
    reset,
    loadRestFallback,
    refresh,
    detachSocketHandlers
  }
})
